import { standardizeString } from '@skop-app/lib-domain/utils'

import { ensurePropIndex } from '@/components/Grid/utils/grid.utils'
import { isNestedKey } from '@/domain/product/persistence/propertyRules'
import type { FormulaEvaluationContext } from '@/types'

import { containsIncrementToken, isMissingFormulaToken } from './formula.config'
import {
  getFormulaDefaultValue as getSharedFormulaDefaultValue,
  isFormulaInvalidMarker,
} from './formula.evaluation'
import { flattenObj } from './reference.handler'

import { extractIdOrValue, getStringLabel, toStringValue } from './value.utils'

type FormulaRow = Record<string, unknown>
type FormulaRowWithProperties = FormulaRow & {
  properties?: Array<{ key: string; values?: unknown; type?: string }>
  __propIndex?: Record<string, number>
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const getPropertyStoredValue = (
  row: FormulaRowWithProperties,
  key: string,
): unknown => {
  ensurePropIndex(row)
  const propertyIndex = row.__propIndex?.[key]
  const property =
    propertyIndex !== undefined && Array.isArray(row.properties)
      ? row.properties[propertyIndex]
      : undefined

  if (!property || property.values == null) {
    return row[key]
  }

  return Array.isArray(property.values) ? property.values[0] : property.values
}

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, string>)
    : undefined
}

const getWrappedValue = (value: unknown): unknown => {
  const record = toRecord(value)
  if (!record || !('value' in record)) return undefined
  return record.value
}

const getLookupCandidate = (value: unknown): string | undefined => {
  const scalar = extractIdOrValue(value)
  if (scalar !== undefined && scalar !== null) {
    const stringValue = String(scalar)
    if (stringValue.length > 0) return stringValue
  }

  const label = getStringLabel(value)
  if (label) return label

  const stringValue = toStringValue(value)
  return stringValue.length > 0 ? stringValue : undefined
}

const getDirectSubKeyValue = (
  value: unknown,
  subKey: string,
): unknown | undefined => {
  const record = toRecord(value)
  if (!record || !Object.prototype.hasOwnProperty.call(record, subKey)) {
    return undefined
  }
  return record[subKey]
}

const getDeepValue = (
  value: unknown,
  pathParts: string[],
): unknown | undefined => {
  if (pathParts.length === 0) return value

  let current: unknown = value
  for (const part of pathParts) {
    if (!current || typeof current !== 'object') return undefined
    const record = current as Record<string, unknown>
    if (!Object.prototype.hasOwnProperty.call(record, part)) {
      return undefined
    }
    current = record[part]
  }

  return current
}

const findOptionInLookup = (
  lookup: Record<string, unknown> | undefined,
  value: unknown,
): Record<string, unknown> | undefined => {
  if (!lookup) return undefined

  const lookupCandidate = getLookupCandidate(value)
  if (!lookupCandidate) return undefined

  const directMatch = lookup[lookupCandidate]
  if (directMatch && typeof directMatch === 'object') {
    return directMatch as Record<string, unknown>
  }

  const normalizedCandidate = standardizeString(lookupCandidate)
  return Object.values(lookup).find((option) => {
    if (!option || typeof option !== 'object') return false
    const optionRecord = option as Record<string, unknown>
    return [optionRecord.id, optionRecord.value, optionRecord.label].some(
      (candidate) => {
        const candidateString = getLookupCandidate(candidate)
        return (
          candidateString !== undefined &&
          standardizeString(candidateString) === normalizedCandidate
        )
      },
    )
  }) as Record<string, unknown> | undefined
}

const toFormulaText = (value: unknown): string => {
  const stringValue = String(value)
  return Number.isNaN(Number(stringValue)) ? `"${stringValue}"` : stringValue
}

const isOpaqueUuid = (value: unknown): boolean =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim())

const shouldSuppressUnresolvedLookupScalar = ({
  lookup,
  matchedOption,
  scalar,
}: {
  lookup: Record<string, unknown> | undefined
  matchedOption: Record<string, unknown> | undefined
  scalar: string | undefined
}): boolean => Boolean(lookup) && !matchedOption && isOpaqueUuid(scalar)

export const getFormulaValue = (
  referenceName: string,
  data: FormulaRow | undefined,
  context: FormulaEvaluationContext,
): string | undefined => {
  const [colKey, ...subPathParts] = referenceName.split('.')
  const subKey = subPathParts[0]
  const subPath = subPathParts.join('.')

  if (subPathParts.length > 0 && isNestedKey(referenceName)) {
    const root = data?.[colKey]
    if (root && typeof root === 'object' && !Array.isArray(root)) {
      const nestedValue = getDeepValue(root, subPathParts)
      if (
        nestedValue !== undefined &&
        nestedValue !== null &&
        nestedValue !== ''
      ) {
        return toFormulaText(nestedValue)
      }
    }

    const flattenedValue = data?.[referenceName]
    const unwrappedFlattenedValue =
      getWrappedValue(flattenedValue) ?? flattenedValue
    if (
      unwrappedFlattenedValue !== undefined &&
      unwrappedFlattenedValue !== null &&
      unwrappedFlattenedValue !== ''
    ) {
      return toFormulaText(unwrappedFlattenedValue)
    }

    return undefined
  }

  let colValue: unknown
  // Properties-first (align with grid makeValueGetter): override wins over top-level
  const rowWithProperties = data as FormulaRowWithProperties
  if (data) {
    ensurePropIndex(rowWithProperties)
    const propertyIndex = rowWithProperties.__propIndex?.[colKey]
    if (
      propertyIndex !== undefined &&
      Array.isArray(rowWithProperties.properties)
    ) {
      const prop = rowWithProperties.properties[propertyIndex]
      if (prop) {
        colValue = prop.values
      }
    }
  }
  if (colValue === undefined && data) {
    colValue = data[colKey]
  }

  const referenceValue = data?.[referenceName]
  const valueToMatch = colValue ?? referenceValue

  const gridValue = Array.isArray(valueToMatch) ? valueToMatch[0] : valueToMatch
  const lookupValue = getWrappedValue(gridValue) ?? gridValue

  const optionsByID = context?.optionsByID?.[colKey]
  const matchedOption = findOptionInLookup(optionsByID, lookupValue)

  if (subPathParts.length > 0) {
    if (matchedOption) {
      const valuesWithMeta = flattenObj(matchedOption)
      const raw = valuesWithMeta[subPath]
      if (raw === undefined || raw === null || raw === '') return undefined
      return toFormulaText(raw)
    }

    const directSubKeyValue =
      subPathParts.length === 1
        ? getDirectSubKeyValue(gridValue, subKey)
        : getDeepValue(gridValue, subPathParts)
    if (
      directSubKeyValue !== undefined &&
      directSubKeyValue !== null &&
      directSubKeyValue !== ''
    ) {
      const directScalar = getLookupCandidate(directSubKeyValue)
      if (
        shouldSuppressUnresolvedLookupScalar({
          lookup: optionsByID,
          matchedOption,
          scalar: directScalar,
        })
      ) {
        return undefined
      }
      return toFormulaText(directSubKeyValue)
    }

    if (subPath === 'label') {
      const label = getStringLabel(gridValue) ?? getLookupCandidate(lookupValue)
      if (
        shouldSuppressUnresolvedLookupScalar({
          lookup: optionsByID,
          matchedOption,
          scalar: label,
        })
      ) {
        return undefined
      }
      return label ? toFormulaText(label) : undefined
    }

    if (subPath === 'value' || subPath === 'id') {
      const scalar = getLookupCandidate(lookupValue)
      if (
        shouldSuppressUnresolvedLookupScalar({
          lookup: optionsByID,
          matchedOption,
          scalar,
        })
      ) {
        return undefined
      }
      return scalar ? toFormulaText(scalar) : undefined
    }

    return undefined
  }

  const lookupCandidate = getLookupCandidate(lookupValue)
  const matchedOptionId = getLookupCandidate(matchedOption?.id)
  const matchedOptionLabel =
    matchedOption && 'label' in matchedOption
      ? getLookupCandidate(matchedOption.label)
      : undefined

  if (
    matchedOption &&
    lookupCandidate !== undefined &&
    matchedOptionId !== undefined &&
    lookupCandidate === matchedOptionId &&
    matchedOptionLabel !== undefined
  ) {
    return toFormulaText(matchedOptionLabel)
  }

  if (
    shouldSuppressUnresolvedLookupScalar({
      lookup: optionsByID,
      matchedOption,
      scalar: lookupCandidate,
    })
  ) {
    return undefined
  }

  // if not a LIST, return the value
  const value = formulaOverride(toStringValue(gridValue)) ?? gridValue
  if (value === undefined || value === null || value === '') return undefined

  // Wrap non-numeric values in quotes so they are interpreted as strings
  return toFormulaText(value)
}

// Verifier si la string de référence est figée par l'utilisateur "=(string)"
export const formulaOverride = (input?: string | null): string | undefined => {
  // if no input or not a string, return
  if (!input || typeof input !== 'string') return
  const regex = /^=\((.*)\)$/
  const match = regex.exec(input)
  return match?.[1]
}

export const getFormulaDefaultValue = (formula?: string): string | undefined =>
  getSharedFormulaDefaultValue(formula)

export const isFormulaDisplayInvalid = (value: unknown): boolean => {
  if (value === '-' || value === null || value === undefined) return true
  return isFormulaInvalidMarker(value)
}

export const hasValidIncrement = (value: string | null | undefined) => {
  if (!hasConcreteFormulaValue(value)) return false
  if (typeof value !== 'string') return false
  if (value.length < 3) return false
  const lastThreeChars = value.slice(-3)
  const num = parseInt(lastThreeChars, 10)
  return value.includes('-') && !isNaN(num) && num > 0 && num <= 999
}

const hasConcreteFormulaValue = (value: string | null | undefined): boolean => {
  if (typeof value !== 'string') return false
  if (isMissingFormulaToken(value)) return false
  const trimmedValue = value.trim()
  return (
    trimmedValue.length > 0 &&
    trimmedValue !== '-' &&
    !isFormulaInvalidMarker(trimmedValue)
  )
}

export const shouldFreezeFormulaEvaluation = (params: {
  rawValue?: string | null
  formula?: string
}): boolean => {
  const { rawValue, formula } = params

  if (formulaOverride(rawValue)) return true

  if (containsIncrementToken(formula)) {
    return hasConcreteFormulaValue(rawValue)
  }

  return hasValidIncrement(rawValue)
}

export const generateIncrement = (
  prefix: string,
  name: string,
  allRowData: FormulaRow[],
) => {
  if (!allRowData?.length) return '001'

  const existingIds = allRowData
    .map((product) => {
      const rawValue = getPropertyStoredValue(
        product as FormulaRowWithProperties,
        name,
      )
      const value = toStringValue(getWrappedValue(rawValue) ?? rawValue)
      const cleanValue = formulaOverride(value) ?? value
      return cleanValue
    })
    .filter((key) => {
      return typeof key === 'string' && key?.startsWith(prefix)
    })

  const maxIncrement = existingIds.reduce((max: number, id: string) => {
    if (typeof id !== 'string' || id.length < 3) return max
    const increment = Number(
      /(\d+)$/.exec(id.slice(prefix.length))?.[1] ?? Number.NaN,
    )
    return isNaN(increment) ? max : Math.max(max, increment)
  }, 0)

  const result = (maxIncrement + 1).toString().padStart(3, '0')
  return isNaN(maxIncrement) ? '001' : result
}
